import React, { useState, useContext, useEffect } from "react";
import { AppContext, DispatchContext } from "../AppContext";
import { makeStyles } from "@material-ui/styles";
import { Button, FormControlLabel, FormLabel, FormGroup, IconButton, InputAdornment, List, ListItem, TextField, Switch } from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import * as actions from '../actions/actions';
import LaunchIcon from '@material-ui/icons/Launch';
import ShareIcon from '@material-ui/icons/Share';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ClearIcon from "@material-ui/icons/Clear";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { trimEmptyLines, generatePassword } from '../helpers/constants';

const useStyles = makeStyles((theme) => ({
    root: {
        overflow: "hidden",
        flexDirection: "column",
        display: "flex",
        overflowY: "auto",
        padding: 0,
        width: '100%'
    },
    text_filed_container: {
        width: '100%'
    },
    form: {
        width: "100%",
        marginTop: theme.spacing(1),
    },
    formGroupPadding: {
        width: "fit-content",
        display: "flex",
        flexDirection: "unset",
    },
    noMargin: {
        margin: "0"
    }
}));

export default function ProjectUploadForm(props) {
    const dispatch = useContext(DispatchContext);
    const state = useContext(AppContext);
    const s = state.strings;
    const classes = useStyles(state);
    const project = props.project;
    const saveProject = props.saveProject;

    const [formData, setFormData] = useState(convertToFormData(project));

    useEffect(() => {
        setFormData(convertToFormData(project));
    }, [JSON.stringify(project)]);

    // convert data as received from API into values suitable to form
    function convertToFormData(project) {
        const formData = {
            UploadFormActive: project.UploadFormActive ? true : false,
            UploadFormHeader: project.UploadFormHeader || "",
            UploadFormDescription: project.UploadFormDescription || "",
            StartDate: project.UploadFormStartDate ? new Date(parseInt(project.UploadFormStartDate * 1000)) : null,
            EndDate: project.UploadFormEndDate ? new Date(parseInt(project.UploadFormEndDate * 1000)) : null,
            AutoPlagCheck: project.UploadFormAutoPlagCheck ? true : false,
            SendReportToSubmitter: project.UploadFormSendReport ? true : false,
            RequestMetadata: project.UploadFormRequestMetadata ? true : false,
            PasswordProtected: project.UploadFormPasswordProtected ? true : false,
            Password: project.UploadFormPassword || "",
        }
        return formData;
    }

    // convert form values into values suitable to API
    function convertToSubmitData(formData) {
        const submitData = {
            UploadFormActive: formData.UploadFormActive ? 1 : 0,
            UploadFormHeader: trimEmptyLines(formData.UploadFormHeader),
            UploadFormDescription: trimEmptyLines(formData.UploadFormDescription),
            UploadFormStartDate: formData.StartDate ? new Date(formData.StartDate).getTime() / 1000 : null,
            UploadFormEndDate: formData.EndDate ? new Date(formData.EndDate).getTime() / 1000 : null,
            UploadFormAutoPlagCheck: formData.AutoPlagCheck ? 1 : 0,
            UploadFormSendReport: formData.SendReportToSubmitter ? 1 : 0,
            UploadFormRequestMetadata: formData.RequestMetadata ? 1 : 0,
            UploadFormPasswordProtected: formData.PasswordProtected ? 1 : 0,
            UploadFormPassword: formData.Password,
        }
        return submitData;
    }

    const onUploadFormActiveChanged = (value) => {
        setFormData({ ...formData, UploadFormActive: value });
        submitForm({ ...formData, UploadFormActive: value });
    };

    const onPasswordProtectedChanged = (value) => {
        const newPassword = value ? generatePassword(8) : "";
        setFormData({ ...formData, PasswordProtected: value, Password: newPassword });
        submitForm({ ...formData, PasswordProtected: value, Password: newPassword });
    };

    const onPasswordCopied = () => {
        dispatch(actions.openToast({ type: "success", message: s.uploadFormular.passwordCopied || "Password copied to clipboard", status: true }));
    };

    const onAutoPlagCheckChanged = (value) => {
        setFormData({ ...formData, AutoPlagCheck: value });
        submitForm({ ...formData, AutoPlagCheck: value });
    };

    const onSendReportToSubmitterChanged = (value) => {
        setFormData({ ...formData, SendReportToSubmitter: value });
        submitForm({ ...formData, SendReportToSubmitter: value });
    };

    const onRequestMetadataChanged = (value) => {
        setFormData({ ...formData, RequestMetadata: value });
        submitForm({ ...formData, RequestMetadata: value });
    };

    const onFormSubmitted = (e) => {
        e.preventDefault();
        submitForm(formData);
    }

    function submitForm(data) {
        const submitData = convertToSubmitData(data);
        saveProject(submitData);
    }

    const onOpenUploadFormButtonClicked = () => {
        let url = (window.location.href).replace('projects', 'upload');
        window.open(url + "/" + project.UploadFormLink, "_blank")
    }

    const onUploadLinkCopied = () => {
        let url = (window.location.href).replace('projects', 'upload');
        navigator.clipboard.writeText(url + '/' + project.UploadFormLink);
        dispatch(actions.openToast({ type: "success", message: s.copiedToClipboard, status: true }));
    }

    const onStartDateChanged = (date) => {
        setFormData({ ...formData, StartDate: date });
        submitForm({ ...formData, StartDate: date })
    }

    const onEndDateChanged = (date) => {
        setFormData({ ...formData, EndDate: date });
        submitForm({ ...formData, EndDate: date })
    }

    return (
        <div className={classes.root}>
            <form className={classes.form} noValidate autoComplete="off" onSubmit={onFormSubmitted}>
                <FormGroup className={classes.formGroupPadding} >
                    <FormControlLabel
                        disabled={!project.IsOwner}
                        className={classes.noMargin}
                        control={<Switch checked={formData.UploadFormActive} onChange={(e) => onUploadFormActiveChanged(e.target.checked)} />}
                        label={<span>
                            {s.useUpload}
                        </span>} />
                    <IconButton onClick={onOpenUploadFormButtonClicked} disabled={!formData.UploadFormActive}>
                        <LaunchIcon />
                    </IconButton>
                    <CopyToClipboard text={(window.location.href).replace('projects', 'upload') + "/" + project.UploadFormLink} onCopy={onUploadLinkCopied}>
                        <IconButton disabled={!formData.UploadFormActive}>
                            <ShareIcon />
                        </IconButton>
                    </CopyToClipboard>
                </FormGroup>
                {/* Temporarily hidden - password protection feature
                <FormGroup className={classes.formGroupPadding} style={{ marginLeft: 16, alignItems: "center" }}>
                    <FormControlLabel
                        disabled={!formData.UploadFormActive || !project.IsOwner}
                        className={classes.noMargin}
                        control={<Switch checked={formData.PasswordProtected} onChange={(e) => onPasswordProtectedChanged(e.target.checked)} />}
                        label={<span>
                            {s.uploadFormular.passwordProtected || "Password protected"}
                        </span>} />
                    {formData.PasswordProtected && formData.Password && (
                        <>
                            <span style={{ 
                                fontFamily: "monospace", 
                                fontSize: 14, 
                                backgroundColor: "#f5f5f5", 
                                padding: "6px 12px", 
                                borderRadius: 4, 
                                marginLeft: 8,
                                border: "1px solid #ddd"
                            }}>
                                {formData.Password}
                            </span>
                            <CopyToClipboard text={formData.Password} onCopy={onPasswordCopied}>
                                <IconButton size="small">
                                    <FileCopyIcon fontSize="small" />
                                </IconButton>
                            </CopyToClipboard>
                        </>
                    )}
                </FormGroup>
                */}
                <List>
                    <ListItem>
                        <div className={classes.text_filed_container}>
                            <TextField
                                className={classes.text_filed_container}
                                label={s.uploadFormHeading}
                                size="small"
                                disabled={!formData.UploadFormActive || !project.IsOwner}
                                value={formData.UploadFormHeader}
                                InputProps={{ endAdornment: "" }}
                                onBlur={onFormSubmitted}
                                onChange={(e) => setFormData({ ...formData, UploadFormHeader: e.target.value })}
                            />
                        </div>
                    </ListItem>
                    <ListItem>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <div style={{ marginRight: 16 }}>
                                <DatePicker
                                    disabled={!formData.UploadFormActive || !project.IsOwner}
                                    label={s.uploadFormular.validFrom}
                                    size="small"
                                    format={s.common.dateFormatJS}
                                    value={formData.StartDate}
                                    onChange={(date) => onStartDateChanged(date)}
                                    InputProps={{
                                        endAdornment: (
                                            formData.StartDate && (
                                                <InputAdornment position="end">
                                                    <IconButton disabled={!formData.UploadFormActive || !project.IsOwner} size={"small"} onClick={(e) => { onStartDateChanged(null); e.stopPropagation(); }} edge="end">
                                                        <ClearIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        ),
                                    }}
                                />
                            </div>
                            <div>
                                <DatePicker
                                    disabled={!formData.UploadFormActive || !project.IsOwner}
                                    label={s.uploadFormular.validUntil}
                                    size="small"
                                    format={s.common.dateFormatJS}
                                    value={formData.EndDate}
                                    onChange={(date) => onEndDateChanged(date)}
                                    InputProps={{
                                        endAdornment: (
                                            formData.EndDate && (
                                                <InputAdornment position="end">
                                                    <IconButton disabled={!formData.UploadFormActive || !project.IsOwner} size={"small"} onClick={(e) => { onEndDateChanged(null); e.stopPropagation(); }} edge="end">
                                                        <ClearIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        ),
                                    }}
                                />
                            </div>
                        </MuiPickersUtilsProvider>
                    </ListItem>
                    <ListItem>
                        <div className={classes.text_filed_container}>
                            <TextField
                                multiline
                                className={classes.text_filed_container}
                                size="small"
                                disabled={!formData.UploadFormActive || !project.IsOwner}
                                label={s.uploadFormText}
                                value={formData.UploadFormDescription || ""}
                                onBlur={onFormSubmitted}
                                onChange={(e) => setFormData({ ...formData, UploadFormDescription: e.target.value })}
                            />
                        </div>
                    </ListItem>
                    <ListItem>
                        <FormControlLabel
                            disabled={!formData.UploadFormActive || !project.IsOwner}
                            control={
                                <Switch
                                    checked={formData.RequestMetadata}
                                    onChange={(e) => onRequestMetadataChanged(e.target.checked)}
                                />
                            }
                            label={s.uploadFormular.requestMetadata || "Request metadata from submitter"}
                        />
                    </ListItem>
                    <ListItem>
                        <FormControlLabel
                            disabled={!formData.UploadFormActive || !project.IsOwner}
                            control={
                                <Switch
                                    checked={formData.AutoPlagCheck}
                                    onChange={(e) => onAutoPlagCheckChanged(e.target.checked)}
                                />
                            }
                            label={s.uploadFormular.autoPlagCheck || "Perform plagiarism check after upload"}
                        />
                    </ListItem>
                    <ListItem>
                        <FormControlLabel
                            disabled={!formData.UploadFormActive || !formData.AutoPlagCheck || !project.IsOwner}
                            control={
                                <Switch
                                    checked={formData.SendReportToSubmitter}
                                    onChange={(e) => onSendReportToSubmitterChanged(e.target.checked)}
                                />
                            }
                            label={s.uploadFormular.sendReportToSubmitter || "Send report to submitter's email"}
                        />
                    </ListItem>
                </List>
                <Button type="submit" style={{ display: "none" }} aria-hidden="true" tabIndex={-1} >
                    Submit
                </Button>
            </form>
        </div>
    );
};
